import { StateMachine } from "@/core/arch/state_machine";
import type { ActivationBonusRepository } from "./activation_bonus_repository";
import {
  DEFAULT_CREATED_RANGE,
  isCreatedRangeInvalid,
  resolveCreatedRange,
  type ActivationBonusCampaignRow,
  type ActivationBonusStage,
  type CreatedRangeFilter,
  type RetryActivationBonusPayoutResult,
} from "./types";

const PAGE_SIZE = 500;

export interface ActivationBonusState {
  items: ActivationBonusCampaignRow[];
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
  nextToken: string | null;
  stageFilter: ActivationBonusStage[];
  createdRange: CreatedRangeFilter;
  retrying: Set<string>;
}

/**
 * Both filters are applied by the query, not in the view: the `empty` stage
 * spans nearly the whole merchant table, so paging it down client-side would
 * mean fetching every merchant to find the handful that matter.
 */
export class ActivationBonusBloc extends StateMachine<ActivationBonusState> {
  private _loadToken = 0;

  constructor(
    private repo: Pick<ActivationBonusRepository, "list" | "retryPayout">,
  ) {
    super({
      items: [],
      status: "idle",
      nextToken: null,
      stageFilter: [],
      createdRange: DEFAULT_CREATED_RANGE,
      retrying: new Set(),
    });
  }

  private queryFilters(): {
    stages: ActivationBonusStage[];
    createdAfter: string | null;
    createdBefore: string | null;
  } {
    return {
      stages: this.state.stageFilter,
      ...resolveCreatedRange(this.state.createdRange),
    };
  }

  async load(): Promise<void> {
    const token = ++this._loadToken;

    // Bumping the token first so any in-flight response is discarded rather
    // than landing on top of the empty result.
    if (isCreatedRangeInvalid(this.state.createdRange)) {
      this.setState({
        ...this.state,
        items: [],
        nextToken: null,
        status: "loaded",
        error: undefined,
      });
      return;
    }

    this.setState({ ...this.state, status: "loading", error: undefined });

    try {
      const page = await this.repo.list({
        limit: PAGE_SIZE,
        ...this.queryFilters(),
      });
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        items: page.items,
        nextToken: page.nextToken,
        status: "loaded",
        error: undefined,
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error:
          err instanceof Error
            ? err.message
            : "Failed to load activation bonus campaigns",
      });
    }
  }

  async loadMore(): Promise<void> {
    if (!this.state.nextToken || this.state.status === "loading") return;
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading" });

    try {
      const page = await this.repo.list({
        nextToken: this.state.nextToken,
        limit: PAGE_SIZE,
        ...this.queryFilters(),
      });
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        items: [...this.state.items, ...page.items],
        nextToken: page.nextToken,
        status: "loaded",
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error:
          err instanceof Error
            ? err.message
            : "Failed to load activation bonus campaigns",
      });
    }
  }

  /** Replaces the stage chips and re-queries from the first page. */
  async setStageFilter(filter: ActivationBonusStage[]): Promise<void> {
    this.setState({ ...this.state, stageFilter: filter });
    await this.reload();
  }

  /** Replaces the account-created window and re-queries from the first page. */
  async setCreatedRange(range: CreatedRangeFilter): Promise<void> {
    this.setState({ ...this.state, createdRange: range });
    await this.reload();
  }

  /**
   * Drops the current page before reloading so a filter change can't leave rows
   * on screen that the new filter excludes.
   */
  private async reload(): Promise<void> {
    this.setState({ ...this.state, items: [], nextToken: null });
    await this.load();
  }

  async retryPayout(
    businessId: string,
  ): Promise<RetryActivationBonusPayoutResult> {
    const retrying = new Set(this.state.retrying);
    retrying.add(businessId);
    this.setState({ ...this.state, retrying });

    try {
      const result = await this.repo.retryPayout(businessId);
      return result;
    } finally {
      const next = new Set(this.state.retrying);
      next.delete(businessId);
      this.setState({ ...this.state, retrying: next });
    }
  }
}
